import axiosInstance from "utils/axios";
import { toast } from "sonner";
import { getCompanyIdFromToken, getUserIdFromToken } from "../utils/tokenUtils";
import { convertToUTC, toUTCISOString, getLocalTime } from "../utils/dateUtils";


// Channel mapping for follow-up types
const getChannelId = (type) => {
  const channelMap = {
    call: 1,
    email: 2,
    meeting: 3,
    whatsapp: 4,
    sms: 5
  };
  return channelMap[type] || 1; // Default to call
};


// Mock data for development - replace with actual API calls
let mockFollowUps = [
  {
    id: "1",
    leadId: "lead-1",
    date: new Date("2024-01-15"),
    time: "10:00",
    type: "call",
    priority: "high",
    notes: "Follow up on pricing discussion",
    assignedTo: "john",
    scheduledBy: "current-user",
    createdAt: new Date("2024-01-10"),
    status: "scheduled",
  },
  {
    id: "2",
    leadId: "lead-2",
    date: new Date("2024-01-16"),
    time: "14:30",
    type: "email",
    priority: "medium",
    notes: "Send course details and brochure",
    assignedTo: "sarah",
    scheduledBy: "current-user",
    createdAt: new Date("2024-01-11"),
    status: "scheduled",
  },
];



// Create a new follow-up
export const createFollowUp = async (followUpData) => {
  try {
    // Convert local selection to UTC
    const isoString = convertToUTC(followUpData.date, followUpData.time || "09:00");
    const utcDate = isoString;
    const utcTime = isoString ? isoString.split('T')[1].substring(0, 5) : "00:00";

    // Prepare the API payload
    const apiPayload = {
      leadId: followUpData.leadId,
      assignedTo: followUpData.assignedTo || getUserIdFromToken(),
      scheduledDate: utcDate, // Send UTC Day
      scheduledTime: utcTime, // Send UTC Time
      channelId: getChannelId(followUpData.type || "call"),
      notes: followUpData.notes || ""
    };


    // Make the API call
    const response = await axiosInstance.post("/api/FollowUp/CreateFollowUp", apiPayload);


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Follow-up scheduled successfully!", {
        description: `Follow-up has been scheduled for ${followUpData.date ? new Date(followUpData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'today'} at ${followUpData.time ? (parseInt(followUpData.time.split(':')[0]) % 12 || 12) + ':' + followUpData.time.split(':')[1] + (parseInt(followUpData.time.split(':')[0]) >= 12 ? ' PM' : ' AM') : '9:00 AM'}`,
        duration: 4000,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Follow-up scheduled successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to schedule follow-up");
    }
  } catch (error) {
    console.error("Error creating follow-up:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to schedule follow-up";

    toast.error("Failed to schedule follow-up", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Get follow-ups for a specific lead
export const getLeadFollowUps = async (leadId) => {
  try {
    // For development, use mock data
    if (process.env.NODE_ENV === "development") {
      const leadFollowUps = mockFollowUps.filter(
        followUp => followUp.leadId === leadId
      );

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      return {
        success: true,
        data: leadFollowUps,
      };
    }

    // Production API call
    const response = await axiosInstance.get(`/api/followups/lead/${leadId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching lead follow-ups:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch follow-ups");
  }
};

// Get all follow-ups with optional filters
export const getFollowUps = async (filters = {}) => {
  try {
    // For development, use mock data
    if (process.env.NODE_ENV === "development") {
      let filteredFollowUps = [...mockFollowUps];

      // Apply filters
      if (filters.status) {
        filteredFollowUps = filteredFollowUps.filter(
          followUp => followUp.status === filters.status
        );
      }

      if (filters.assignedTo) {
        filteredFollowUps = filteredFollowUps.filter(
          followUp => followUp.assignedTo === filters.assignedTo
        );
      }

      if (filters.date) {
        const filterDate = new Date(filters.date);
        filteredFollowUps = filteredFollowUps.filter(
          followUp => followUp.date.toDateString() === filterDate.toDateString()
        );
      }

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      return {
        success: true,
        data: filteredFollowUps,
      };
    }

    // Production API call
    const response = await axiosInstance.get("/api/followups", { params: filters });
    return response.data;
  } catch (error) {
    console.error("Error fetching follow-ups:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch follow-ups");
  }
};

// Update follow-up (full update using new POST endpoint)
export const updateFollowUp = async (followUpId, followUpData) => {
  try {
    // Convert local selection to UTC
    const isoString = convertToUTC(followUpData.date, followUpData.time || "09:00");
    const utcDate = isoString;
    const utcTime = isoString ? isoString.split('T')[1].substring(0, 5) : "00:00";

    // Prepare the API payload according to the new API structure
    const apiPayload = {
      followUpId: followUpId,
      notes: followUpData.notes || followUpData.remarks || "",
      assignedTo: followUpData.assignedTo || getUserIdFromToken(),
      newScheduledDate: utcDate,
      newScheduledTime: utcTime,
      channelId: followUpData.channelId || getChannelId(followUpData.type || "call"), // Prefer original channelId; fall back to type mapping
      updatedBy: followUpData.updatedBy || getUserIdFromToken()
    };


    // Make the API call using new POST endpoint
    const response = await axiosInstance.post('/api/FollowUp/UpdateFollowUp', apiPayload);


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Follow-up updated successfully!", {
        description: `Follow-up has been updated for ${followUpData.date ? new Date(followUpData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'today'} at ${followUpData.time ? (parseInt(followUpData.time.split(':')[0]) % 12 || 12) + ':' + followUpData.time.split(':')[1] + (parseInt(followUpData.time.split(':')[0]) >= 12 ? ' PM' : ' AM') : '9:00 AM'}`,
        duration: 4000,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Follow-up updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update follow-up");
    }
  } catch (error) {
    console.error("Error updating follow-up:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to update follow-up";

    toast.error("Failed to update follow-up", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Update follow-up status (using new POST endpoint)
export const updateFollowUpStatus = async (followUpId, statusData = {}) => {
  try {

    // Skip mock data and always use real API for status updates
    // if (process.env.NODE_ENV === "development") {
    //   ... mock code removed to force real API calls
    // }

    let utcDate = null;
    let utcTime = null;

    if (statusData.newScheduledDate) {
      const isoString = convertToUTC(statusData.newScheduledDate, statusData.newScheduledTime || "09:00");
      if (isoString) {
        utcDate = isoString;
        utcTime = isoString.split('T')[1].substring(0, 5);
      }
    }

    // Prepare API payload according to the new API structure
    const apiPayload = {
      followUpId: followUpId,
      eventTypeId: statusData.eventTypeId || statusData.outcomeId || "3fa85f64-5717-4562-b3fc-2c963f66afa6",
      remarks: statusData.remarks || "",
      statusId: statusData.statusId || 0,
      assignedTo: statusData.assignedTo || getUserIdFromToken(),
      newScheduledDate: utcDate ? utcDate : (statusData.newScheduledDate ? toUTCISOString(statusData.newScheduledDate) : null), // Fallback
      newScheduledTime: utcTime ? utcTime : (statusData.newScheduledTime || "09:00"),
      updatedBy: getUserIdFromToken()
    };


    // Production API call using new POST endpoint
    const response = await axiosInstance.post('/api/FollowUp/UpdateFollowUp', apiPayload);


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Follow-up status updated successfully!", {
        description: `Status updated successfully`,
        duration: 4000,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Follow-up status updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update follow-up status");
    }
  } catch (error) {
    console.error("Error updating follow-up status:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to update follow-up status";

    toast.error("Failed to update follow-up status", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Update multiple follow-ups in bulk (using new POST endpoint)
/**
 * Get follow-ups by lead ID
 * @param {string} leadId - The lead ID to fetch follow-ups for
 * @returns {Promise<Object>} API response with follow-ups data
 */
export const getFollowUpsByLeadId = async (leadId) => {
  try {
    if (!leadId) {
      throw new Error("Lead ID is required");
    }


    const response = await axiosInstance.get(`/api/FollowUp/GetFollowUpsByLeadId?LeadId=${leadId}`);
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data string if it's a JSON string
      let followUpsData = [];
      if (typeof responseData.data === "string") {
        try {
          followUpsData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing follow-ups data:", parseError);
          followUpsData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        followUpsData = responseData.data;
      }

      // Localize scheduledTime and apply Overdue logic
      followUpsData = followUpsData.map(detail => {
        // Logic: If Pending (1) AND (Date < Today OR (Date == Today AND Time >= 20:00)) -> Overdue (6)
        let statusId = detail.StatusId;
        let statusName = detail.StatusName;

        if (statusId === 1) {
          const scheduledDate = new Date(detail.ScheduledDate);
          const today = new Date();

          // Compare dates (reset time to 00:00:00)
          const sDate = new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate());
          const tDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

          if (sDate < tDate) {
            statusId = 6;
            statusName = 'Overdue';
          } else if (sDate.getTime() === tDate.getTime()) {
            if (today.getHours() >= 20) {
              statusId = 6;
              statusName = 'Overdue';
            }
          }
        }

        return {
          ...detail,
          StatusId: statusId,
          StatusName: statusName,
          // Update lowercase props if they exist to maintain consistency
          statusId: statusId,
          status: statusName,
          scheduledTime: getLocalTime(detail.ScheduledDate, detail.ScheduledTime)
        };
      });


      return {
        success: true,
        data: followUpsData,
        message: responseData.message,
        totalRecords: responseData.totalRecords || followUpsData.length,
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch follow-ups");
    }
  } catch (error) {
    console.error("❌ Error fetching follow-ups by lead ID:", error);

    let errorMessage = "Failed to fetch follow-ups";
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;

      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status || null,
      data: [],
      totalRecords: 0,
    };
  }
};

export const updateMultipleFollowUps = async (followUpIds, updateData = {}) => {
  try {

    // Prepare the API payload according to the new API structure
    // Only send fields that are being updated - exclude schedule time/date for status updates
    const apiPayload = {
      followUpIds: followUpIds,
      companyId: updateData.companyId || null,
      eventTypeId: updateData.eventTypeId || null,
      remarks: updateData.remarks || "",
      assignedTo: updateData.assignedTo || null,
      statusId: updateData.statusId || null,
      updatedBy: updateData.updatedBy || getUserIdFromToken()
    };

    // Only include schedule fields if they are explicitly provided
    if (updateData.newScheduledDate) {
      apiPayload.newScheduledDate = updateData.newScheduledDate;
    }
    if (updateData.newScheduledTime) {
      apiPayload.newScheduledTime = updateData.newScheduledTime;
    }


    // Make the API call using new POST endpoint
    const response = await axiosInstance.post('/api/FollowUp/UpdateMultiplefollowUps', apiPayload);


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Follow-ups updated successfully!", {
        description: `${followUpIds.length} follow-up(s) have been updated`,
        duration: 4000,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Follow-ups updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update follow-ups");
    }
  } catch (error) {
    console.error("Error updating multiple follow-ups:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to update follow-ups";

    toast.error("Failed to update follow-ups", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Bulk update follow-up status
export const bulkUpdateFollowUpStatus = async (followUpIds, statusData = {}) => {
  try {

    // Use the new bulk update API - only send outcome, status, and remarks
    // Do NOT send schedule time/date for status updates
    return await updateMultipleFollowUps(followUpIds, {
      statusId: statusData.statusId,
      remarks: statusData.remarks,
      assignedTo: statusData.assignedTo,
      priority: statusData.priority,
      companyId: statusData.companyId,
      eventTypeId: statusData.eventTypeId,
      updatedBy: statusData.updatedBy || getUserIdFromToken()
      // Explicitly excluding newScheduledDate and newScheduledTime
    });
  } catch (error) {
    console.error("Error bulk updating follow-up status:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to update follow-up status";

    toast.error("Failed to bulk update follow-ups", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Reschedule follow-up
export const rescheduleFollowUp = async (rescheduleData) => {
  try {

    // Convert local selection to UTC
    let utcDate = null;
    let utcTime = null;
    if (rescheduleData.newScheduledDate) {
      // Use convertToUTC to get proper ISO string
      const isoString = convertToUTC(rescheduleData.newScheduledDate, rescheduleData.newScheduledTime || "09:00");
      if (isoString) {
        utcDate = isoString;
        utcTime = isoString.split('T')[1].substring(0, 5);
      }
    }

    // Prepare the API payload according to the API structure in image
    const apiPayload = {
      followUpId: rescheduleData.followUpId,
      notes: rescheduleData.notes || "",
      newScheduledDate: utcDate,
      newScheduledTime: utcTime,
      chanelId: rescheduleData.channelId || 0 // Note strict spelling "chanelId" as per requirements
    };


    // Make the API call
    const response = await axiosInstance.post('/api/FollowUp/RescheduleFollowUp', apiPayload);


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Follow-up rescheduled successfully!", {
        description: `Follow-up rescheduled to ${rescheduleData.newScheduledDate ? new Date(rescheduleData.newScheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'selected date'} at ${rescheduleData.newScheduledTime ? (parseInt(rescheduleData.newScheduledTime.split(':')[0]) % 12 || 12) + ':' + rescheduleData.newScheduledTime.split(':')[1] + (parseInt(rescheduleData.newScheduledTime.split(':')[0]) >= 12 ? ' PM' : ' AM') : 'selected time'}`,
        duration: 4000,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Follow-up rescheduled successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to reschedule follow-up");
    }
  } catch (error) {
    console.error("Error rescheduling follow-up:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to reschedule follow-up";

    toast.error("Failed to reschedule follow-up", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Delete follow-up
export const deleteFollowUp = async (followUpId) => {
  try {
    // For development, use mock data
    if (process.env.NODE_ENV === "development") {
      const followUpIndex = mockFollowUps.findIndex(f => f.id === followUpId);
      if (followUpIndex === -1) {
        throw new Error("Follow-up not found");
      }

      mockFollowUps.splice(followUpIndex, 1);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      return {
        success: true,
        message: "Follow-up deleted successfully",
      };
    }

    // Production API call
    const response = await axiosInstance.delete(`/api/followups/${followUpId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting follow-up:", error);
    throw new Error(error.response?.data?.message || "Failed to delete follow-up");
  }
};

// Get upcoming follow-ups for dashboard
export const getUpcomingFollowUps = async (days = 7) => {
  try {
    // For development, use mock data
    if (process.env.NODE_ENV === "development") {
      const today = new Date();
      const futureDate = new Date();
      futureDate.setDate(today.getDate() + days);

      const upcomingFollowUps = mockFollowUps.filter(followUp => {
        const followUpDate = new Date(followUp.date);
        return followUpDate >= today && followUpDate <= futureDate && followUp.status === "scheduled";
      });

      // Sort by date and time
      upcomingFollowUps.sort((a, b) => {
        const dateA = new Date(`${a.date.toDateString()} ${a.time}`);
        const dateB = new Date(`${b.date.toDateString()} ${b.time}`);
        return dateA - dateB;
      });

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      return {
        success: true,
        data: upcomingFollowUps,
      };
    }

    // Production API call
    const response = await axiosInstance.get(`/api/followups/upcoming`, { params: { days } });
    return response.data;
  } catch (error) {
    console.error("Error fetching upcoming follow-ups:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch upcoming follow-ups");
  }
};

// Get follow-up events for status updates
export const getFollowUpEvents = async () => {
  try {

    const response = await axiosInstance.get("/api/FollowUp/GetFollowUpEvents");


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      // Parse the data field if it's a string
      let eventsData = response.data.data;
      if (typeof eventsData === 'string') {
        try {
          eventsData = JSON.parse(eventsData);
        } catch (parseError) {
          console.error("Error parsing events data:", parseError);
          eventsData = [];
        }
      }

      if (!Array.isArray(eventsData)) {
        eventsData = [];
      }

      // Transform the data for the dropdown
      const events = eventsData.map(event => ({
        id: event.EventTypeId,
        name: event.EventName,
        description: event.EventDescription,
        icon: event.Icon,
        defaultScore: event.DefaultScore,
        isFollowUpEvent: event.IsFollowUpEvent,
        value: event.EventTypeId,
        label: event.EventName
      }));

      return {
        success: true,
        data: events,
        totalRecords: response.data.totalRecords || eventsData.length,
        message: response.data.message || "Follow-up events fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch follow-up events");
    }
  } catch (error) {
    console.error("Error fetching follow-up events:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to fetch follow-up events";

    toast.error("Failed to load follow-up events", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0
    };
  }
};

// Get follow-up statistics
export const getFollowUpStats = async () => {
  try {
    // For development, use mock data
    if (process.env.NODE_ENV === "development") {
      const stats = {
        total: mockFollowUps.length,
        scheduled: mockFollowUps.filter(f => f.status === "scheduled").length,
        completed: mockFollowUps.filter(f => f.status === "completed").length,
        overdue: mockFollowUps.filter(f => {
          const followUpDate = new Date(`${f.date.toDateString()} ${f.time}`);
          return followUpDate < new Date() && f.status === "scheduled";
        }).length,
        today: mockFollowUps.filter(f => {
          const today = new Date().toDateString();
          return f.date.toDateString() === today && f.status === "scheduled";
        }).length,
      };

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      return {
        success: true,
        data: stats,
      };
    }

    // Production API call
    const response = await axiosInstance.get("/api/followups/stats");
    return response.data;
  } catch (error) {
    console.error("Error fetching follow-up stats:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch follow-up statistics");
  }
};

// Get users for assignment dropdown (uses the new GetUsersForAssignLeads API)
export const getUsersForAssignment = async () => {
  try {
    const response = await axiosInstance.get("/api/User/GetUsersForAssignLeads");


    // Check if the response indicates failure (case-insensitive)
    if (response.data?.status === "Failure" || response.data?.statusCode >= 400) {
      throw new Error(response.data?.message || "Failed to fetch users for assignment");
    }

    if (response.data.statusCode === 200 && response.data.status?.toLowerCase() === "success") {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === 'string') {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          userData = [];
        }
      }

      if (!Array.isArray(userData)) {
        userData = [];
      }

      // Transform the data for the dropdown - simpler format without query count
      const users = userData.map(user => ({
        id: user.UserId,
        name: user.FullName,
        value: user.UserId,
        label: user.FullName
      }));

      return {
        success: true,
        data: users,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Users fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error fetching users for assignment:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to fetch users";

    toast.error("Failed to load assignment options", {
      description: errorMessage,
      duration: 5000,
    });

    // Return current user as fallback
    const currentUserId = getUserIdFromToken();
    return {
      success: false,
      error: errorMessage,
      data: currentUserId ? [{
        id: currentUserId,
        name: "Myself",
        value: currentUserId,
        label: "Myself"
      }] : [],
      totalRecords: currentUserId ? 1 : 0
    };
  }
};

// Get follow-ups by date range for calendar view (new API)
// isApplication: 0 = All, 2 = Applicants only, 1 = Leads only
export const getFollowUpsByDateRange = async (fromDate, toDate, isApplication = 0) => {
  try {

    const payload = {
      fromDate: fromDate,
      toDate: toDate,
      type: isApplication
    };

    const response = await axiosInstance.post("/api/FollowUp/GetCalenderByFilters", payload);


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      // Parse the data field if it's a string
      let followUpData = response.data.data;
      if (typeof followUpData === 'string') {
        try {
          followUpData = JSON.parse(followUpData);
        } catch (parseError) {
          console.error("Error parsing follow-up data:", parseError);
          followUpData = [];
        }
      }

      if (!Array.isArray(followUpData)) {
        followUpData = [];
      }

      // Transform the data for calendar consumption
      const transformedData = {};
      followUpData.forEach(dayData => {
        // FollowUpDay is already in YYYY-MM-DD format, no need to split
        const dateKey = dayData.FollowUpDay;
        transformedData[dateKey] = {
          date: dayData.FollowUpDay,
          weekDayName: dayData.WeekDayName,
          totalFollowUps: dayData.TotalFollowUps,
          pendingFollowUps: dayData.PendingFollowUps,
          completedFollowUps: dayData.CompletedFollowUps,
          cancelledFollowUps: dayData.CancelledFollowUps,
          rescheduledFollowUps: dayData.RescheduledFollowUps,
          details: dayData.FollowUpDetails?.map(detail => ({
            id: detail.FollowUpId || detail.Id, // Use the actual follow-up ID from backend
            userId: detail.UserId,
            userName: detail.UserName,
            leadname: detail.leadname, // Lead name from new API
            LeadName: detail.LeadName, // Alternative lead name field
            fullName: detail.FullName, // Assigned user's full name
            status: detail.StatusName || detail.Status, // Use StatusName from API
            statusId: detail.StatusId, // Include status ID
            outcomeId: detail.OutcomeId, // Include outcome ID if available
            eventName: detail.EventName, // Event name if available
            remarks: detail.Remarks,
            notes: detail.Notes,
            scheduledTime: getLocalTime(dayData.FollowUpDay, detail.ScheduledTime),
            leadId: detail.LeadId, // Include lead ID if available
            LeadId: detail.LeadId, // Alternative lead ID field for compatibility
            priority: detail.Priority, // Include priority from API
            assignedTo: detail.AssignedTo, // Include assigned to from API
            channelId: detail.ChannelId, // Include channel ID
            ChannelName: detail.ChannelName || detail.channelName, // Include channel name for type resolution
            type: detail.Type || detail.ChannelId, // Include type/channel if available
            Email: detail.Email, // Include email from API
            email: detail.Email, // Alternative email field for compatibility
            PhoneNumber: detail.PhoneNumber, // Include phone number from API
            phoneNumber: detail.PhoneNumber, // Alternative phone field for compatibility
            mobile: detail.PhoneNumber, // Alternative mobile field for compatibility
            isApplicant: detail.IsApplicant, // Whether this is an applicant or lead
            IsApplicant: detail.IsApplicant // Alternative field for compatibility
          })) || []
        };
      });

      return {
        success: true,
        data: transformedData,
        rawData: followUpData,
        totalRecords: response.data.totalRecords || followUpData.length,
        message: response.data.message || "Calendar follow-ups fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch calendar follow-ups");
    }
  } catch (error) {
    console.error("Error fetching calendar follow-ups:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to fetch calendar follow-ups";

    toast.error("Failed to load calendar data", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: {},
      rawData: [],
      totalRecords: 0
    };
  }
};

// Get follow-ups by month and year for calendar view (legacy API - kept for backward compatibility)
export const getFollowUpsByMonthAndYear = async (month, year) => {
  try {

    const response = await axiosInstance.get("/api/FollowUp/GetFollowupsBymonthAndyear", {
      params: {
        Month: month,
        Year: year
      }
    });


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      // Parse the data field if it's a string
      let followUpData = response.data.data;
      if (typeof followUpData === 'string') {
        try {
          followUpData = JSON.parse(followUpData);
        } catch (parseError) {
          console.error("Error parsing follow-up data:", parseError);
          followUpData = [];
        }
      }

      if (!Array.isArray(followUpData)) {
        followUpData = [];
      }

      // Transform the data for calendar consumption
      const transformedData = {};
      followUpData.forEach(dayData => {
        // FollowUpDay is already in YYYY-MM-DD format, no need to split
        const dateKey = dayData.FollowUpDay;
        transformedData[dateKey] = {
          date: dayData.FollowUpDay,
          weekDayName: dayData.WeekDayName,
          totalFollowUps: dayData.TotalFollowUps,
          pendingFollowUps: dayData.PendingFollowUps,
          completedFollowUps: dayData.CompletedFollowUps,
          cancelledFollowUps: dayData.CancelledFollowUps,
          rescheduledFollowUps: dayData.RescheduledFollowUps,
          details: dayData.FollowUpDetails?.map(detail => ({
            id: detail.FollowUpId || detail.Id, // Use the actual follow-up ID from backend
            userId: detail.UserId,
            userName: detail.UserName,
            leadname: detail.leadname, // Lead name from new API
            LeadName: detail.LeadName, // Alternative lead name field
            fullName: detail.FullName, // Assigned user's full name
            status: detail.StatusName || detail.Status, // Use StatusName from API
            statusId: detail.StatusId, // Include status ID
            outcomeId: detail.OutcomeId, // Include outcome ID if available
            eventName: detail.EventName, // Event name if available
            remarks: detail.Remarks,
            notes: detail.Notes,
            scheduledTime: getLocalTime(dayData.FollowUpDay, detail.ScheduledTime),
            leadId: detail.LeadId, // Include lead ID if available
            LeadId: detail.LeadId, // Alternative lead ID field for compatibility
            priority: detail.Priority, // Include priority from API
            assignedTo: detail.AssignedTo, // Include assigned to from API
            channelId: detail.ChannelId, // Include channel ID
            ChannelName: detail.ChannelName || detail.channelName, // Include channel name for type resolution
            type: detail.Type || detail.ChannelId, // Include type/channel if available
            Email: detail.Email, // Include email from API
            email: detail.Email, // Alternative email field for compatibility
            PhoneNumber: detail.PhoneNumber, // Include phone number from API
            phoneNumber: detail.PhoneNumber, // Alternative phone field for compatibility
            mobile: detail.PhoneNumber, // Alternative mobile field for compatibility
            isApplicant: detail.IsApplicant, // Whether this is an applicant or lead
            IsApplicant: detail.IsApplicant // Alternative field for compatibility
          })) || []
        };
      });

      return {
        success: true,
        data: transformedData,
        rawData: followUpData,
        totalRecords: response.data.totalRecords || followUpData.length,
        message: response.data.message || "Monthly follow-ups fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch monthly follow-ups");
    }
  } catch (error) {
    console.error("Error fetching monthly follow-ups:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to fetch monthly follow-ups";

    toast.error("Failed to load calendar data", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: {},
      rawData: [],
      totalRecords: 0
    };
  }
};
